import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtINR, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Trash, Plus } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

export default function InvoiceForm() {
  const nav = useNavigate();
  const { company } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState([{ name: "", hsn: "", quantity: 1, price: 0, tax_rate: 18 }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/customers").then(({ data }) => setCustomers(data));
    api.get("/products").then(({ data }) => setProducts(data));
  }, []);

  const customer = customers.find((c) => c.id === customerId);
  const sellerState = company?.gstin?.slice(0, 2);
  const buyerState = customer?.gstin?.slice(0, 2) || sellerState;
  const intraState = sellerState && sellerState === buyerState;

  const totals = useMemo(() => {
    let sub = 0, cgst = 0, sgst = 0, igst = 0;
    lines.forEach((l) => {
      const amt = (Number(l.quantity) || 0) * (Number(l.price) || 0);
      const tax = amt * (Number(l.tax_rate) || 0) / 100;
      sub += amt;
      if (intraState) { cgst += tax / 2; sgst += tax / 2; }
      else igst += tax;
    });
    return {
      sub: +sub.toFixed(2), cgst: +cgst.toFixed(2), sgst: +sgst.toFixed(2),
      igst: +igst.toFixed(2), total: +(sub + cgst + sgst + igst).toFixed(2),
    };
  }, [lines, intraState]);

  const setLine = (i, patch) => setLines(lines.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const addLine = () => setLines([...lines, { name: "", hsn: "", quantity: 1, price: 0, tax_rate: 18 }]);
  const removeLine = (i) => setLines(lines.length > 1 ? lines.filter((_, idx) => idx !== i) : lines);

  const pickProduct = (i, pid) => {
    const p = products.find((x) => x.id === pid);
    if (p) setLine(i, { product_id: pid, name: p.name, hsn: p.hsn, price: p.price, tax_rate: p.tax_rate });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!customerId) return toast.error("Select customer");
    if (!lines.length) return toast.error("Add at least one line");
    setSaving(true);
    try {
      const { data } = await api.post("/invoices", {
        customer_id: customerId, invoice_date: invoiceDate, due_date: dueDate || invoiceDate,
        lines: lines.map((l) => ({
          product_id: l.product_id || null, name: l.name, hsn: l.hsn,
          quantity: Number(l.quantity), price: Number(l.price), tax_rate: Number(l.tax_rate),
        })),
        notes,
      });
      toast.success(`Invoice ${data.invoice_no} created`);
      nav(`/app/invoices/${data.id}`);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5 lb-fade" data-testid="invoice-form-page">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold">New</div>
        <h1 className="font-display font-extrabold text-3xl tracking-tight">Sales Invoice</h1>
      </div>

      <div className="lb-card p-6 space-y-4">
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="lb-label">Customer</label>
            <select required className="lb-select" value={customerId}
                    onChange={(e) => setCustomerId(e.target.value)} data-testid="if-customer">
              <option value="">Select customer</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="lb-label">Invoice Date</label>
            <input type="date" required className="lb-input" value={invoiceDate}
                   onChange={(e) => setInvoiceDate(e.target.value)} data-testid="if-date" />
          </div>
          <div>
            <label className="lb-label">Due Date</label>
            <input type="date" className="lb-input" value={dueDate}
                   onChange={(e) => setDueDate(e.target.value)} data-testid="if-due" />
          </div>
        </div>

        {customer && (
          <div className="border border-neutral-200 p-3 bg-neutral-50 text-sm font-mono">
            {customer.name} · {customer.gstin || "No GSTIN (B2C)"} · {customer.state}
            <span className="ml-3 lb-badge">{intraState ? "Intra-state" : "Inter-state"}</span>
          </div>
        )}
      </div>

      <div className="lb-card">
        <table className="lb-table">
          <thead>
            <tr>
              <th style={{ width: 200 }}>Item / Product</th>
              <th>Description</th>
              <th>HSN</th>
              <th className="text-right">Qty</th>
              <th className="text-right">Price</th>
              <th className="text-right">GST %</th>
              <th className="text-right">Amount</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => {
              const amt = (Number(l.quantity) || 0) * (Number(l.price) || 0);
              return (
                <tr key={i}>
                  <td>
                    <select className="lb-select" value={l.product_id || ""}
                            onChange={(e) => pickProduct(i, e.target.value)} data-testid={`if-line-product-${i}`}>
                      <option value="">— pick or type below —</option>
                      {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </td>
                  <td>
                    <input className="lb-input" value={l.name} onChange={(e) => setLine(i, { name: e.target.value })}
                           placeholder="Item name" required data-testid={`if-line-name-${i}`} />
                  </td>
                  <td>
                    <input className="lb-input font-mono" value={l.hsn} onChange={(e) => setLine(i, { hsn: e.target.value })}
                           placeholder="HSN" data-testid={`if-line-hsn-${i}`} />
                  </td>
                  <td>
                    <input type="number" min="0" step="0.01" className="lb-input text-right font-mono"
                           value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })}
                           data-testid={`if-line-qty-${i}`} />
                  </td>
                  <td>
                    <input type="number" min="0" step="0.01" className="lb-input text-right font-mono"
                           value={l.price} onChange={(e) => setLine(i, { price: e.target.value })}
                           data-testid={`if-line-price-${i}`} />
                  </td>
                  <td>
                    <input type="number" min="0" step="0.01" className="lb-input text-right font-mono"
                           value={l.tax_rate} onChange={(e) => setLine(i, { tax_rate: e.target.value })}
                           data-testid={`if-line-tax-${i}`} />
                  </td>
                  <td className="amt">{fmtINR(amt)}</td>
                  <td>
                    <button type="button" onClick={() => removeLine(i)} className="text-neutral-500 hover:text-rose-600"
                            data-testid={`if-line-remove-${i}`}>
                      <Trash size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="p-3 border-t border-neutral-200">
          <button type="button" onClick={addLine} className="lb-btn lb-btn-ghost lb-btn-sm" data-testid="if-add-line">
            <Plus size={12} /> Add line
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-5">
        <div className="lb-card p-5">
          <label className="lb-label">Notes / Terms</label>
          <textarea className="lb-textarea" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)}
                    data-testid="if-notes" />
        </div>
        <div className="lb-card p-5 space-y-2 font-mono">
          <Row label="Subtotal" v={totals.sub} />
          {intraState ? (
            <>
              <Row label={`CGST`} v={totals.cgst} />
              <Row label={`SGST`} v={totals.sgst} />
            </>
          ) : (
            <Row label={`IGST`} v={totals.igst} />
          )}
          <div className="border-t border-neutral-300 pt-2 flex justify-between font-bold text-lg">
            <span>Total</span><span data-testid="if-total">{fmtINR(totals.total)}</span>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <button type="button" onClick={() => nav(-1)} className="lb-btn lb-btn-ghost">Cancel</button>
        <button className="lb-btn" disabled={saving} data-testid="if-submit">
          {saving ? "Saving…" : "Save & Generate IRN →"}
        </button>
      </div>
    </form>
  );
}

function Row({ label, v }) {
  return (
    <div className="flex justify-between text-sm">
      <span>{label}</span><span>{fmtINR(v)}</span>
    </div>
  );
}
